import * as React from "react";
import { ILineItem } from "../models/ILineItem.model";
import BasketContext from "../services/BasketContext";
import BasketService from "../services/BasketService";
import Logger from "../../../gulp/scripts/typescript/modules/Logger";

export interface IQuantityPickerProps extends ILineItem {
	setLoading: (loading: boolean)=> void;
	isLoading: boolean;
}

function QuantityPicker(props: IQuantityPickerProps) {

	const [errorMessage, setErrorMessage] = React.useState("");
	const [quantity, setQuantity] = React.useState(props.Quantity);
	const [hasLoaded, setHasLoaded] = React.useState(false);
	const basketContext = React.useContext(BasketContext);
	const basketService = new BasketService();
	const [debouncer, setDebouncer] = React.useState(null);
	function debounce(f: any, duration: number) {
		clearTimeout(debouncer);
		setDebouncer(setTimeout(f, duration));
	}

	React.useEffect(() => {
		setHasLoaded(false);
		setQuantity(props.Quantity);
		checkValueForErrors(props.Quantity);
		setHasLoaded(true);
	}, [props.Quantity]);

	React.useEffect(() => {
		if(!hasLoaded) {
			setHasLoaded(true);
			checkValueForErrors(quantity);
			return;
		}
		debounce(() => {
			if(errorMessage) {
				return;
			}
			props.setLoading(true);
			basketService.updateLineItemQuantity(props.Guid, quantity).then((data)=> {
				basketContext.setBasket(data._embedded.basket);
				props.setLoading(false);
			})
			.catch((error: Error)=> {
				setErrorMessage("An error has occurred. Please try again.");
				props.setLoading(false);
				Logger.trackException(error, 'Unknown error on update line item quantity endpoint in basket');
			});

		}, 500);

  }, [quantity]);

	function handleOnChange(e: React.FormEvent<HTMLInputElement>) {
		let nextQuantity = parseInt(e.currentTarget.value);
		checkValueForErrors(nextQuantity);
		if(isNaN(nextQuantity)) {
			nextQuantity = null;
		}
		if(nextQuantity === 0) {
			e.currentTarget.blur();
		}
		setQuantity(nextQuantity);
	}

	function decreaseQuantity(e: React.MouseEvent) {
		const nextQuantity = quantity - 1;
		checkValueForErrors(nextQuantity);
		setQuantity(quantity - 1);
	}

	function increaseQuantity(e: React.MouseEvent) {
		const nextQuantity = quantity + 1;
		checkValueForErrors(nextQuantity);
		setQuantity(nextQuantity);
	}

	function checkValueForErrors(value: number) {
		if(isNaN(value)) {
			setErrorMessage("Enter a quantity");
			return false;
		}
		if(value > props.QuantityMax) {
			setErrorMessage(`${props.QuantityMax} is the maximum quantity available`);
			return false;
		}
		setErrorMessage("");
		return true;
	}

	function handleFormSubmit(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
	}

	return (
		<form className="c-add-product" onSubmit={(e)=>handleFormSubmit(e)}>
			<div>
				<label className="c-add-product__label" htmlFor={`basket-quantity-${props.Guid}`}>Quantity</label>
				<div className="c-add-product__controls c-add-product--slim">
					<button className="c-button c-button--full c-button--secondary c-add-product__controls__less"
					onClick={(e)=>decreaseQuantity(e)}
					type="button" data-test="basket-product-decrease-quantity"
									disabled={quantity === 1 || !quantity || props.isLoading}>
						<span className="u-visuallyhidden">Remove one {props.DisplayName} from your basket</span>
					</button>
					<input type="text" disabled={props.isLoading} name={`quantity-${props.Guid}`} id={`basket-quantity-${props.Guid}`} className={`c-add-product__controls__text ${errorMessage ? 'c-add-product__controls__text--input-error' : ''}`} value={quantity} onChange={(e)=> handleOnChange(e)} pattern="^[0-9]*$" data-value-before-change={quantity} data-min="1" data-max={props.QuantityMax} maxLength={2} minLength={1} data-test="basket-item-quantity" inputMode="numeric" aria-invalid={errorMessage ? 'true' : 'false'}  aria-describedby={errorMessage ? `quantity-error-${props.Guid}` : ''}/>
					<button className="c-button c-button--full c-button--secondary c-add-product__controls__more" type="button" data-test="basket-product-increase-quantity"
					onClick={(e)=>increaseQuantity(e)}
									disabled={quantity >= props.QuantityMax || props.isLoading}>
						<span className="u-visuallyhidden">Add one {props.DisplayName} to your basket</span>
					</button>
				</div>
				{errorMessage?
					<p id={`quantity-error-${props.Guid}`} className="c-add-product__message-error" data-test="basket-product-item-message">
						{errorMessage}
					</p>
				: null}
				<input type="hidden" name="id" value={props.ProductName} data-test="product-in-basket-id" />
				<input type="hidden" name="guid" value={props.Guid} data-test="product-in-basket-guid" />
			</div>
		</form>
	);


}

export default QuantityPicker;
